/**
 * Portal authentication against the central Melio auth-service, via the portal backend
 * (`apps/api`) — the browser never sees the OAuth client secret, the authorization code or
 * the refresh token, only the session cookie the backend sets.
 */

export interface AccessTokenResponse {
  /** Short-lived auth-service access token — the Bearer credential for `/api/portal/*` and `/v2`. */
  accessToken: string;
  /** ISO expiry of the access token, when derivable, so the client can refresh proactively. */
  expiresAt?: string;
  email?: string;
  name?: string;
}

export type SsoProvider = 'google';

export interface StartLoginOptions {
  /** `signup` lands on the account-creation screen instead of sign-in. */
  screen?: 'signup';
  ssoProvider?: SsoProvider;
  /** Portal path to come back to once signed in. */
  returnTo?: string;
}

/**
 * The single endpoint that touches the httpOnly refresh cookie: it refreshes and returns a fresh
 * short-lived access token (+ identity). Called on load and whenever the token nears expiry. Throws
 * on a 401 (no/expired session) so the caller can treat the user as signed out.
 */
export async function refreshAccessToken(signal?: AbortSignal): Promise<AccessTokenResponse> {
  const res = await fetch('/api/auth/token/refresh', { method: 'POST', credentials: 'include', signal });
  if (res.status === 401) throw new Error('unauthenticated');
  if (!res.ok) throw new Error(`Token refresh failed with status ${res.status}`);
  return (await res.json()) as AccessTokenResponse;
}

/**
 * Leaves the SPA for the backend's login route, which redirects on to auth-service's authorize
 * endpoint and the Melio account UI — sign-in, create-account or straight into an SSO provider.
 * The portal renders none of it, and comes back to `/signin/callback`.
 */
export function startLogin({ screen, ssoProvider, returnTo }: StartLoginOptions = {}): void {
  const url = new URL('/api/auth/login', window.location.origin);
  if (screen) url.searchParams.set('screen', screen);
  if (ssoProvider) url.searchParams.set('sso_provider', ssoProvider);
  if (returnTo) url.searchParams.set('returnTo', returnTo);
  window.location.assign(url.toString());
}

const RETURN_TO_FALLBACK = '/dashboard';

/** A `\` raw or percent-encoded: no portal path has one, and browsers differ on how they re-read it. */
const hasBackslash = (value: string): boolean => {
  if (value.includes('\\')) return true;
  try {
    return decodeURIComponent(value).includes('\\');
  } catch {
    return true;
  }
};

/**
 * Mirrors the backend's own guard on the way in, so the path we navigate to is a normalized
 * same-origin portal path even if the response carried something else. The resolved path is what
 * gets returned, so anything the URL parser reinterprets is discarded rather than passed through.
 */
const safeReturnTo = (value: unknown): string => {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return RETURN_TO_FALLBACK;

  let resolved: URL;
  try {
    resolved = new URL(value, window.location.origin);
  } catch {
    return RETURN_TO_FALLBACK;
  }
  if (resolved.origin !== window.location.origin) return RETURN_TO_FALLBACK;

  const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;
  return hasBackslash(value) || hasBackslash(path) ? RETURN_TO_FALLBACK : path;
};

/**
 * The code a failed `/auth/callback` reports, as a single string.
 *
 * The backend answers in one of two shapes: `{ error: 'invalid_request' | 'email_not_allowed' | … }`
 * for the cases the route decides itself, or the app-wide envelope
 * `{ error: { message, code } }` when something threw on the way (`AUTH_SERVICE_TOKEN_ERROR` from
 * the token exchange, say). Both end up in a `?error=` param, so both have to reduce to a string —
 * handing the envelope to `new Error()` stringified it to `[object Object]`, which named no error,
 * matched no known code and told the user nothing.
 */
const callbackErrorCode = (body: unknown): string => {
  const error = (body as { error?: unknown } | null | undefined)?.error;
  if (typeof error === 'string' && error) return error;
  if (error && typeof error === 'object') {
    const { code, message } = error as { code?: unknown; message?: unknown };
    if (typeof code === 'string' && code) return code;
    if (typeof message === 'string' && message) return message;
  }
  return 'exchange_failed';
};

/**
 * Hands the `code` auth-service returned to `/signin/callback` over to the backend, which is the
 * only side holding the client secret and the PKCE verifier. Resolves with the portal path the
 * sign-in started from.
 */
export async function completeLogin(code: string, state: string): Promise<string> {
  const res = await fetch('/api/auth/callback', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, state }),
  });

  // A failure ahead of the app (a gateway 502, an HTML error page) has no JSON body to read, and its
  // parse error must not become the reported code either.
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) throw new Error(callbackErrorCode(body));
  return safeReturnTo((body as { returnTo?: unknown } | null)?.returnTo);
}

/**
 * Full sign-out. Clears the portal `dp_session` (server side) and then sends the browser to
 * auth-app's logout, which ends the central Melio auth-service (SSO) session and redirects back to
 * the portal's `/signin` — so a subsequent sign-in actually re-authenticates instead of silently
 * resuming the SSO session. Falls back to `/signin` if the backend returned no logout URL.
 */
export async function logout(): Promise<void> {
  let logoutUrl: string | undefined;
  try {
    const res = await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    if (res.ok) logoutUrl = ((await res.json()) as { logoutUrl?: string }).logoutUrl;
  } catch {
    // Network failure — still redirect to /signin below so the user isn't stranded signed-in.
  }
  window.location.assign(logoutUrl ?? '/signin');
}
